import * as THREE from "three";

import { fittedCameraQuaternion, fittedVerticalFov } from "./camera";
import type { ScenePhoto } from "./types";

export type TourPhase = "hold" | "retreat" | "orbit" | "approach";

export interface TourSample {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  fov: number;
  phase: TourPhase;
  photoIndex: number;
  targetPhotoIndex: number;
  phaseProgress: number;
}

interface TourSegment {
  phase: TourPhase;
  source: number;
  target: number;
  start: number;
  duration: number;
}

const HOLD_SECONDS = 3.2;
const RETREAT_SECONDS = 1.6;
const APPROACH_SECONDS = 1.9;
const OVERVIEW_FOV = 48;
const ACTIVE_PHOTO_OPACITY = 0.5;
const SPATIAL_PHOTO_OPACITY = 0.24;

function ease(value: number): number {
  return value < 0.5 ? 4 * value ** 3 : 1 - (-2 * value + 2) ** 3 / 2;
}

export function photoOpacity(sample: TourSample, photoIndex: number): number {
  if (sample.phase === "hold") {
    return photoIndex === sample.photoIndex ? ACTIVE_PHOTO_OPACITY : 0;
  }
  if (sample.phase === "approach") {
    return photoIndex === sample.targetPhotoIndex
      ? THREE.MathUtils.lerp(
          SPATIAL_PHOTO_OPACITY,
          ACTIVE_PHOTO_OPACITY,
          sample.phaseProgress,
        )
      : 0;
  }
  if (sample.phase === "retreat") {
    return photoIndex === sample.photoIndex
      ? THREE.MathUtils.lerp(
          ACTIVE_PHOTO_OPACITY,
          SPATIAL_PHOTO_OPACITY,
          sample.phaseProgress,
        )
      : THREE.MathUtils.lerp(0, SPATIAL_PHOTO_OPACITY, sample.phaseProgress);
  }
  return SPATIAL_PHOTO_OPACITY;
}

function shortestAngleDelta(from: number, to: number): number {
  return (
    THREE.MathUtils.euclideanModulo(to - from + Math.PI, Math.PI * 2) - Math.PI
  );
}

function lookAtQuaternion(
  position: THREE.Vector3,
  target: THREE.Vector3,
): THREE.Quaternion {
  const camera = new THREE.PerspectiveCamera();
  camera.up.set(0, 0, 1);
  camera.position.copy(position);
  camera.lookAt(target);
  return camera.quaternion.clone();
}

function backedPosition(
  photo: ScenePhoto,
  center: THREE.Vector3,
): THREE.Vector3 {
  const position = new THREE.Vector3().fromArray(photo.camera.position);
  const radial = position.clone().sub(center);
  radial.z = 0;
  if (radial.lengthSq() < 1e-8) radial.set(0, -1, 0);
  const retreat = Math.max(4, position.distanceTo(center) * 0.28);
  return position.add(radial.normalize().multiplyScalar(retreat));
}

function makeSegments(
  photos: ScenePhoto[],
  center: THREE.Vector3,
): TourSegment[] {
  const segments: TourSegment[] = [];
  let start = 0;
  photos.forEach((_photo, source) => {
    const target = (source + 1) % photos.length;
    const sourceBack = backedPosition(photos[source], center);
    const targetBack = backedPosition(photos[target], center);
    const sourceAngle = Math.atan2(
      sourceBack.y - center.y,
      sourceBack.x - center.x,
    );
    const targetAngle = Math.atan2(
      targetBack.y - center.y,
      targetBack.x - center.x,
    );
    const angle = Math.abs(shortestAngleDelta(sourceAngle, targetAngle));
    const orbitDuration = THREE.MathUtils.clamp(2.4 + angle * 0.8, 2.4, 4.8);
    for (const [phase, duration] of [
      ["hold", HOLD_SECONDS],
      ["retreat", RETREAT_SECONDS],
      ["orbit", orbitDuration],
      ["approach", APPROACH_SECONDS],
    ] as const) {
      segments.push({ phase, source, target, start, duration });
      start += duration;
    }
  });
  return segments;
}

export function tourDuration(
  photos: ScenePhoto[],
  center: THREE.Vector3,
): number {
  const segments = makeSegments(photos, center);
  const last = segments.at(-1);
  return last ? last.start + last.duration : 0;
}

export function photoStartTime(
  photos: ScenePhoto[],
  center: THREE.Vector3,
  photoIndex: number,
): number {
  return (
    makeSegments(photos, center).find(
      (segment) => segment.phase === "hold" && segment.source === photoIndex,
    )?.start ?? 0
  );
}

export function sampleTour(
  elapsedSeconds: number,
  photos: ScenePhoto[],
  center: THREE.Vector3,
  viewportAspect: number,
): TourSample {
  if (photos.length === 0)
    throw new Error("Prohlídka vyžaduje alespoň jednu fotografii.");
  const segments = makeSegments(photos, center);
  const duration = tourDuration(photos, center);
  const time = THREE.MathUtils.euclideanModulo(elapsedSeconds, duration);
  const segment =
    segments.find(
      (candidate) =>
        time >= candidate.start && time < candidate.start + candidate.duration,
    ) ?? segments[0];
  const rawProgress = (time - segment.start) / segment.duration;
  const progress = ease(THREE.MathUtils.clamp(rawProgress, 0, 1));
  const source = photos[segment.source];
  const target = photos[segment.target];
  const sourcePosition = new THREE.Vector3().fromArray(source.camera.position);
  const targetPosition = new THREE.Vector3().fromArray(target.camera.position);
  const sourceQuaternion = fittedCameraQuaternion(source.camera);
  const targetQuaternion = fittedCameraQuaternion(target.camera);
  const sourceFov = fittedVerticalFov(source, viewportAspect);
  const targetFov = fittedVerticalFov(target, viewportAspect);
  const sourceBack = backedPosition(source, center);
  const targetBack = backedPosition(target, center);

  if (segment.phase === "hold") {
    return {
      position: sourcePosition,
      quaternion: sourceQuaternion,
      fov: sourceFov,
      phase: segment.phase,
      photoIndex: segment.source,
      targetPhotoIndex: segment.target,
      phaseProgress: rawProgress,
    };
  }
  if (segment.phase === "retreat") {
    return {
      position: sourcePosition.clone().lerp(sourceBack, progress),
      quaternion: sourceQuaternion
        .clone()
        .slerp(lookAtQuaternion(sourceBack, center), progress),
      fov: THREE.MathUtils.lerp(sourceFov, OVERVIEW_FOV, progress),
      phase: segment.phase,
      photoIndex: segment.source,
      targetPhotoIndex: segment.target,
      phaseProgress: rawProgress,
    };
  }
  if (segment.phase === "approach") {
    return {
      position: targetBack.clone().lerp(targetPosition, progress),
      quaternion: lookAtQuaternion(targetBack, center).slerp(
        targetQuaternion,
        progress,
      ),
      fov: THREE.MathUtils.lerp(OVERVIEW_FOV, targetFov, progress),
      phase: segment.phase,
      photoIndex: segment.target,
      targetPhotoIndex: segment.target,
      phaseProgress: rawProgress,
    };
  }

  const sourceOffset = sourceBack.clone().sub(center);
  const targetOffset = targetBack.clone().sub(center);
  const sourceAngle = Math.atan2(sourceOffset.y, sourceOffset.x);
  const targetAngle = Math.atan2(targetOffset.y, targetOffset.x);
  const angle =
    sourceAngle + shortestAngleDelta(sourceAngle, targetAngle) * progress;
  const radius = THREE.MathUtils.lerp(
    sourceOffset.length(),
    targetOffset.length(),
    progress,
  );
  const position = new THREE.Vector3(
    center.x + Math.cos(angle) * radius,
    center.y + Math.sin(angle) * radius,
    THREE.MathUtils.lerp(sourceBack.z, targetBack.z, progress),
  );
  return {
    position,
    quaternion: lookAtQuaternion(position, center),
    fov: OVERVIEW_FOV,
    phase: segment.phase,
    photoIndex: segment.source,
    targetPhotoIndex: segment.target,
    phaseProgress: rawProgress,
  };
}
